/**
 * Pharmacist queue search, instant + server (Rx-01).
 *
 * Typing filters the rows already loaded at once (`matchesSearch`); the server search runs after
 * a short pause and, when it answers, its rows replace the instant ones (it also sees orders not
 * loaded yet). Its rows are fresher, so they are written back over any cached copy of the same
 * order (`refreshCachedRows`) — clearing the search then shows the updated rows, never two
 * versions of one order.
 */

import type { InfiniteData, QueryClient } from "@tanstack/react-query";

import type { PaginatedQueue, QueueOrder } from "../api/orders";

/** Mirrors the backend (OrderRepo.queue): the order reference or the patient's name, anywhere,
 *  case-insensitive; the phone when the term has 3+ digits. */
export function matchesSearch(order: QueueOrder, term: string): boolean {
  const t = term.trim().toLowerCase();
  if (!t) return true;
  if (order.order_id.toLowerCase().includes(t)) return true;
  if (order.patient_name?.toLowerCase().includes(t)) return true;
  const digits = t.replace(/\D/g, "");
  return digits.length >= 3 && (order.patient_phone ?? "").replace(/\D/g, "").includes(digits);
}

type Cached = InfiniteData<PaginatedQueue> | PaginatedQueue;

function replaceRows(page: PaginatedQueue, fresh: Map<string, QueueOrder>): PaginatedQueue {
  let changed = false;
  const data = page.data.map((row) => {
    const next = fresh.get(row.id);
    if (!next || JSON.stringify(next) === JSON.stringify(row)) return row;
    changed = true;
    return next;
  });
  return changed ? { ...page, data } : page;
}

/** Copy the server's versions of `rows` over the same orders in every cached queue list. */
export function refreshCachedRows(qc: QueryClient, rows: QueueOrder[]): void {
  if (rows.length === 0) return;
  const fresh = new Map(rows.map((row) => [row.id, row]));
  for (const [key, cached] of qc.getQueriesData<Cached>({ queryKey: ["queue"] })) {
    if (!cached) continue;
    if ("pages" in cached) {
      const pages = cached.pages.map((page) => replaceRows(page, fresh));
      if (pages.some((page, i) => page !== cached.pages[i])) qc.setQueryData(key, { ...cached, pages });
    } else {
      const page = replaceRows(cached, fresh);
      if (page !== cached) qc.setQueryData(key, page);
    }
  }
}

/** Local calendar dates (yyyy-mm-dd) → the server's order-time bounds: from start of `from` to
 *  start of the day after `to` (exclusive). Null when the range is backwards. */
export function dateRange(from: string, to: string): { created_from?: string; created_to?: string } | null {
  const start = from ? localMidnight(from, 0) : undefined;
  const end = to ? localMidnight(to, 1) : undefined;
  if (start && end && start >= end) return null;
  return { created_from: start?.toISOString(), created_to: end?.toISOString() };
}

function localMidnight(date: string, addDays: number): Date {
  const [y, m, d] = date.split("-").map(Number);
  return new Date(y, m - 1, d + addDays);
}
